import { AddIcon } from '@approvalmax/ui';
import {
    Box,
    Button,
    ColumnDefinition,
    Form,
    Grid,
    RowActionMenuItem,
    Select,
    Table,
    Text,
} from '@approvalmax/ui/src/components';
import { useVendorTemplateFieldSettings } from 'pages/requestForm/containers/netsuite/hooks';
import { isFieldVisible } from 'pages/requestForm/containers/netsuite/NetSuiteRequestForm/NetSuiteRequestForm.helpers';
import { memo, useCallback, useMemo } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';

import { FormValues } from '../../NetSuiteVendorCreationPopupContent.types';
import { getColumnDefinition } from './SubsidiariesInformation.config';
import { messages } from './SubsidiariesInformation.messages';
import { TableErrorHint } from './SubsidiariesInformation.styles';
import { SubsidiariesInformationProps } from './SubsidiariesInformation.types';

export const SubsidiariesInformation = memo<SubsidiariesInformationProps>((props) => {
    const { subsidiaries, currenciesMap } = props;

    const fieldSettings = useVendorTemplateFieldSettings();

    const { control, getFieldState, formState, getValues } = useFormContext<FormValues>();

    const { fields, append, remove } = useFieldArray({
        control,
        name: 'subsidiaries',
    });

    const { error } = getFieldState('subsidiaries', formState);

    const columnData = useMemo(() => {
        return fields.map((field, index) => ({
            ...field,
            index,
        }));
    }, [fields]);

    const handleRowAdd = useCallback(() => {
        append({});
    }, [append]);

    const handleCloneLine = useCallback(
        (index: number) => {
            const subsidiariesValues = getValues('subsidiaries');

            const data = subsidiariesValues?.[index] ?? {};

            append(data);
        },
        [append, getValues]
    );

    const columns = useMemo<ColumnDefinition<(typeof columnData)[number]>[]>(
        () =>
            getColumnDefinition({
                subsidiaries,
                control,
                currenciesMap,
            }),
        [control, currenciesMap, subsidiaries]
    );

    const rowActionMenuItems = useMemo<RowActionMenuItem<(typeof columnData)[number]>[]>(
        () => [
            {
                label: messages.cloneLine,
                onClick: (item) => handleCloneLine(item.index),
            },
            {
                label: messages.removeLine,
                onClick: (item) => remove(item.index),
            },
        ],
        [handleCloneLine, remove]
    );

    return (
        <Form.Part>
            <Text font='headline' fontSize='xxsmall' fontWeight='medium' color='midnight80' spacing='20 0'>
                {messages.subsidiariesSection}
            </Text>

            <Grid gridTemplateColumns='1fr' gap={16}>
                <Grid gridTemplateColumns='1fr 1fr' gap={16}>
                    <Select
                        label={messages.primarySubsidiary}
                        required
                        name='primarySubsidiaryId'
                        placeholder={messages.selectPlaceholder}
                        autocomplete
                        items={subsidiaries}
                        clearable={false}
                    />
                </Grid>

                {isFieldVisible(fieldSettings.subsidiaries) && (
                    <div>
                        <Table columns={columns} data={columnData} bordered rowActionMenuItems={rowActionMenuItems} />

                        {error?.message && <TableErrorHint>{error.message}</TableErrorHint>}

                        <Box spacing='8 0'>
                            <Button size='small' color='blue80' onClick={handleRowAdd} startIcon={<AddIcon />}>
                                {messages.addLineButtonText}
                            </Button>
                        </Box>
                    </div>
                )}
            </Grid>
        </Form.Part>
    );
});

SubsidiariesInformation.displayName = 'SubsidiariesInformation';
